import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { resetTable, selectionStateToSelectedIds } from "../../../../components/table/utils";
import { HStack, VStack, Wrap } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import {
  type ProductQueryV4Row,
  useGetV4ProductReportByProduct,
} from "../../../perf_reports/api/product/v4ProductReportByProduct";
import { useQuery } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../../components/table/useBlockWindowQuery";
import { useGetV4ProductReportOptions } from "../../../perf_reports/api/product/v4ProductReportOptions";
import { useV4ProductReportExport } from "../../../perf_reports/api/product/v4ProductReportExport";
import { toTitleCase } from "../../../../utils/formatText";
import { useServerTable, type AppColumns } from "../../../../components/table/tableFeatures";
import { css } from "../../../../../styled-system/css";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../../../components/custom/merchandise/constants";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { RT8FilterWidget } from "./FilterWidget";
import { ENROLL_OPTIONS } from "../../../advertising/filters/productLifecycleFilters";
import { Table } from "../../../../components/table/Table";
import { DownloadV4Report } from "../../../perf_reports/DownloadReport";
import ProductCard from "../../../../components/custom/merchandise/ProductCard";
import { useAppSelector } from "../../../../redux/hooks";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../../components/table/usePersistedTableState";
import { BulkActionButton } from "../../../../components/table/BulkActionButton";
import BulkTagsForm from "../../../../components/core/blocks/BulkTagsForm";
import { useBulkUpdateAllProductAdStatuses } from "../../../advertising/api/useBulkUpdateAllProductAdStatuses";
import { useBulkUpdateProductProperties } from "../../../advertising/api/useBulkUpdateProductProperties";
import { TableErrorWrapper } from "../../../../components/table/TableErrorWrapper";
import { useSimpleCan } from "../../../../config/casl/casl";
import { DataViewPicker } from "../../../dataViews/components/DataViewPicker";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useTagFilterOptions } from "../../../tags/api/useTagFilterOptions";
import { EntityTagsFormModal } from "../../../tags/components/EntityTagsFormModal";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";

type Props = {
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  categoryId?: string;
  defaultVisibleColumns?: string[];
  persistKey: string;
  columns: AppColumns<ProductQueryV4Row>;
};

const ProductsTable = ({ categoryId, state, initialState, setters, columns, persistKey }: Props) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const [filledTags, setFilledTags] = useState<string[]>([]);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const userCanUpdateProducts = useSimpleCan("update", "Product");

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const byProductOptionsGetter = useGetV4ProductReportByProduct(categoryId);
  const productByProductReportQuery = useBlockWindowQuery(byProductOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debounceSearch,
  });

  const exportMutation = useV4ProductReportExport(state.columnFilters, categoryId);
  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());
  const tagEnhancedOptions = useTagFilterOptions("PRODUCT", { enabled: tagsEnhancedEnabled });

  const tagOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.merchandising["All Tags"]) },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags, t],
  );

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }
    return [
      { value: "all", label: t(($) => $.merchandising["All Variants"]) },
      ...Object.keys(variations).flatMap((variationCategory) => {
        return (
          variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          })) ?? []
        );
      }),
    ];
  }, [productReportOptionsQuery.data?.data, t]);

  const table = useServerTable({
    data: productByProductReportQuery.data?.data,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: () => true,
    getRowId: (row) => row.product_id.toString(),
    enableRowSelection: userCanUpdateProducts,
    rowCount: productByProductReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton:
        productByProductReportQuery.isFetching &&
        (!productByProductReportQuery.data || productByProductReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        variations: variantOptions.filter(Boolean),
        inventory_status: [
          {
            value: "all",
            label: t(($) => $.merchandising["All Inventory States"]),
            description: t(($) => $.merchandising["Show products with any inventory state"]),
          },
          ...INVENTORY_STATE_OPTIONS.filter(Boolean),
        ],
        pricing_plan_status: ENROLL_OPTIONS,
        primary_variant: [
          {
            value: "all",
            label: t(($) => $.merchandising["All Products"]),
          },
          {
            value: true,
            label: t(($) => $.merchandising["Yes"]),
          },
          {
            value: false,
            label: t(($) => $.merchandising["No"]),
          },
        ],
        sales_state: [
          {
            value: "all",
            label: t(($) => $.merchandising["All Sales States"]),
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
      },
    },
  });

  const bulkUpdateProductAdStatusMutation = useBulkUpdateAllProductAdStatuses();
  const bulkUpdateProductPropertiesMutation = useBulkUpdateProductProperties();

  const updateProductAdStatus = (newStatus: string) => {
    const productData = {
      product_ids: selected.map((id) => parseInt(id)),
      status: newStatus,
    };

    bulkUpdateProductAdStatusMutation.mutate(productData);
  };

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  return (
    <div>
      {userCanUpdateProducts && (
        <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
          <BulkActionButton
            selected={selected}
            options={[
              ...(tagsEnhancedEnabled ?
                ([
                  {
                    label: t(($) => $.merchandising["Add Product Tags"]),
                    action: () => {
                      void NiceModal.show(EntityTagsFormModal, {
                        entityType: "PRODUCT",
                        title: t(($) => $.merchandising["Add Product Tags"]),
                        onSubmit: async (tagIds) => {
                          await bulkUpdateProductPropertiesMutation.mutateAsync({
                            product_ids: selected.map((id) => parseInt(id)),
                            updates: [{ field: "tags", action: "add", value: tagIds }],
                          });
                        },
                      });
                    },
                  },
                  {
                    label: t(($) => $.merchandising["Remove Product Tags"]),
                    action: () => {
                      void NiceModal.show(EntityTagsFormModal, {
                        entityType: "PRODUCT",
                        title: t(($) => $.merchandising["Remove Product Tags"]),
                        onSubmit: async (tagIds) => {
                          await bulkUpdateProductPropertiesMutation.mutateAsync({
                            product_ids: selected.map((id) => parseInt(id)),
                            updates: [{ field: "tags", action: "remove", value: tagIds }],
                          });
                        },
                      });
                    },
                  },
                ] as const)
              : [
                  {
                    label: t(($) => $.merchandising["Add Product Tags"]),
                    action: () => {
                      bulkUpdateProductPropertiesMutation.mutate(
                        {
                          product_ids: selected.map((id) => parseInt(id)),
                          updates: [
                            {
                              field: "tags" as const,
                              action: "add" as const,
                              value: filledTags,
                            },
                          ],
                        },
                        { onSuccess: () => setFilledTags([]) },
                      );
                    },
                    actionFormFields: (
                      <BulkTagsForm
                        filledTags={filledTags}
                        setFilledTags={setFilledTags}
                        selected={selected}
                        suggestedTags={tagOptions.filter((tag) => tag.value !== "all").map((tag) => tag.value)}
                        taggedItemDescription={"product"}
                        actionType={"add"}
                      />
                    ),
                    confirmationMessage: (
                      <p>
                        {t(
                          ($) =>
                            $.merchandising["Are you sure you want to add the selected tags to the selected products?"],
                          {
                            tagCount: filledTags.length,
                            productCount: selected.length,
                          },
                        )}
                      </p>
                    ),
                  },
                  {
                    label: t(($) => $.merchandising["Remove Product Tags"]),
                    action: () => {
                      bulkUpdateProductPropertiesMutation.mutate(
                        {
                          product_ids: selected.map((id) => parseInt(id)),
                          updates: [
                            {
                              field: "tags" as const,
                              action: "remove" as const,
                              value: filledTags,
                            },
                          ],
                        },
                        { onSuccess: () => setFilledTags([]) },
                      );
                    },
                    actionFormFields: (
                      <BulkTagsForm
                        filledTags={filledTags}
                        setFilledTags={setFilledTags}
                        selected={selected}
                        suggestedTags={tagOptions.filter((tag) => tag.value !== "all").map((tag) => tag.value)}
                        taggedItemDescription={"product"}
                        actionType={"remove"}
                      />
                    ),
                    confirmationMessage: (
                      <p>
                        {t(
                          ($) =>
                            $.merchandising[
                              "Are you sure you want to remove the selected tags from the selected products?"
                            ],
                          {
                            tagCount: filledTags.length,
                            productCount: selected.length,
                          },
                        )}
                      </p>
                    ),
                  },
                ]),
              {
                label: t(($) => $.merchandising["Pause Product Advertisements"]),
                action: () => {
                  updateProductAdStatus("paused");
                },
                confirmationMessage: (
                  <p>
                    {t(
                      ($) =>
                        $.merchandising[
                          "Are you sure you want to pause all product advertisements for the selected products?"
                        ],
                      { count: selected.length },
                    )}
                  </p>
                ),
              },
              {
                label: t(($) => $.merchandising["Enable Product Advertisements"]),
                action: () => {
                  updateProductAdStatus("enabled");
                },
                confirmationMessage: (
                  <p>
                    {t(
                      ($) =>
                        $.merchandising[
                          "Are you sure you want to enable all product advertisements for the selected products?"
                        ],
                      { count: selected.length },
                    )}
                  </p>
                ),
              },
            ]}
          />
        </Wrap>
      )}

      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button variant={"contentOnly"} onPress={() => resetTable(table, persistKey)} fontSize={"xxlarge"}>
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey={persistKey} datasetKey="product" table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper
        error={productByProductReportQuery.error}
        userMessage={t(($) => $.merchandising["Error loading product report."])}
      >
        <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={(row) => (
            <ProductCard
              productId={row.original.product_id}
              marketPlace={marketPlace}
              user={user}
              channel={channel}
              initialTab={"overview"}
              additionalTabs={[]}
              hasBorder={false}
            />
          )}
        />
      </TableErrorWrapper>
      <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack>
    </div>
  );
};

export default ProductsTable;
